import React, { useRef, useState } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { navigate } from '@reach/router';
import axios from 'axios';

import BackLink from '../../components/BackLink';
import BoomerItemPreview from './BoomerItemPreview';
import defaultBoomer from './images/default-boomer.png';
import plusCircled from '../../images/plus-circled.svg';
import { BOOMER_ITEM_CATEGORIES, DEFAULT_COORDINATES } from './categories';
import { CREATE_AVATAR_ITEM, CREATE_SPECIAL_STORE_OPENING, GET_S3_UPLOAD_QUERY, EDIT_AVATAR_ITEM, EDIT_SPECIAL_STORE_OPENING, DELETE_AVATAR_ITEM, GET_SPECIAL_STORE_OPENINGS } from './services/graphql';
import styles from './BoomerItems.module.scss';

const itemTypes = BOOMER_ITEM_CATEGORIES.filter(category => [1, 2, 3, 4, 6].includes(category.itemType));
const newItem = () => ({ name: '', gender: '', itemType: '', price: '', preview: '', url: '', x: 0, y: 0 });

const hasItemDetails = item => Boolean(item.name || item.gender || item.itemType || item.price !== '' || item.preview || item.url);

const AddSpecialStore = ({ opening }) => {
    const client = useApolloClient();
    const [startDate, setStartDate] = useState(opening ? opening.startDate : '');
    const [endDate, setEndDate] = useState(opening ? opening.endDate : '');
    const [items, setItems] = useState(() => {
        const existing = opening ? opening.avatarItems.map(item => ({
            id: item.id, name: item.name || '',
            gender: { female: '1', male: '2', mixed: '3' }[item.gender] || '',
            itemType: String((BOOMER_ITEM_CATEGORIES.find(category => category.type === item.type) || {}).itemType || ''),
            price: item.price == null ? '' : item.price,
            preview: item.previewUrl, url: item.previewUrl,
            x: item.xOffset == null ? 0 : item.xOffset, y: item.yPos == null ? 0 : item.yPos,
        })) : [];
        return [...existing, ...Array.from({ length: Math.max(0, 5 - existing.length) }, newItem)];
    });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [started, setStarted] = useState(false);
    const [complete, setComplete] = useState(false);
    const [error, setError] = useState('');
    const openingId = useRef(opening ? opening.id : null);
    const [removedIds, setRemovedIds] = useState([]);
    const deletedIds = useRef([]);
    const savedItems = useRef([]);
    const selectedItem = items[selectedIndex];
    const locked = uploading || saving || complete;
    const positionLocked = locked || Boolean(savedItems.current[selectedIndex]);
    const skin = items.find(item => Number(item.itemType) === 1 && item.preview);
    const isSkin = selectedItem && Number(selectedItem.itemType) === 1;

    const updateItem = (index, changes) => {
        setItems(previous => previous.map((item, itemIndex) => itemIndex === index ? { ...item, ...changes } : item));
    };

    const selectType = (index, value) => {
        updateItem(index, { itemType: value, ...(DEFAULT_COORDINATES[value] || { x: 0, y: 0 }) });
        setSelectedIndex(index);
    };

    const uploadItem = (event, index) => {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file) return;
        const extension = file.name.split('.').pop().toLowerCase();
        if (!['jpg', 'jpeg', 'png', 'svg'].includes(extension)) {
            setError('Only .jpg, .jpeg, .png, and .svg files are allowed.');
            return;
        }
        setError('');
        setUploading(true);
        setSelectedIndex(index);
        updateItem(index, { url: '', preview: '' });
        const onError = () => {
            updateItem(index, { preview: '', url: '' });
            setError(`Item ${index + 1} could not be uploaded. Please select the image again.`);
            setUploading(false);
        };
        const reader = new FileReader();
        reader.onerror = onError;
        reader.onload = () => {
            updateItem(index, { preview: reader.result });
            client.query({
                query: GET_S3_UPLOAD_QUERY,
                variables: {
                    name: `avatarItemResources-${Date.now()}-${index}.${extension}`,
                    fileType: extension,
                    object: 'avatarItemResources',
                },
            }).then(response => {
                const uploadUrl = response.data.s3UploadUrlFor.uploadUrl;
                return axios.put(uploadUrl, file, { headers: { 'Content-Type': '' } }).then(() => {
                    updateItem(index, { url: uploadUrl.split('?')[0] });
                    setUploading(false);
                });
            }).catch(onError);
        };
        reader.readAsDataURL(file);
    };

    const changePosition = (axis, value) => {
        if (/^-?\d*$/.test(value)) updateItem(selectedIndex, { [axis]: value });
    };

    const moveItem = (x, y) => {
        updateItem(selectedIndex, { x: (Number(selectedItem.x) || 0) + x, y: (Number(selectedItem.y) || 0) + y });
    };

    const createStore = (event) => {
        event.preventDefault();
        if (saving || uploading || complete) return;
        if (!startDate || !endDate || endDate <= startDate) {
            setError('Choose a start date and an end date after the start date.');
            return;
        }
        if (!opening && !items.some(hasItemDetails)) {
            setError('Add at least one item.');
            return;
        }
        const invalidIndex = items.findIndex(item => hasItemDetails(item) && (
            !item.name.trim() || !item.gender || !item.itemType || (!item.id && !item.url) ||
            item.price === '' || !Number.isInteger(Number(item.price)) || Number(item.price) < 0 ||
            ['x', 'y'].some(axis => !/^-?\d+$/.test(String(item[axis])) || Number(item[axis]) < -2147483648 || Number(item[axis]) > 2147483647)
        ));
        if (!items.length || invalidIndex !== -1) {
            setError(`Complete the name, gender, type, image, gem cost, and position for item ${invalidIndex + 1}.`);
            return;
        }
        setError('');
        setSaving(true);

        const onError = (failure) => {
            setSaving(false);
            setError(failure.message);
        };
        const saveItem = (index) => {
            if (index === items.length) {
                client.query({ query: GET_SPECIAL_STORE_OPENINGS, fetchPolicy: 'network-only' })
                    .then(() => {
                        setSaving(false);
                        setComplete(true);
                        navigate('/boomerItems/specialStore');
                    }).catch(onError);
                return;
            }
            if (savedItems.current[index] || !hasItemDetails(items[index])) {
                saveItem(index + 1);
                return;
            }
            const item = items[index];
            client.mutate({
                mutation: item.id ? EDIT_AVATAR_ITEM : CREATE_AVATAR_ITEM,
                variables: {
                    name: item.name.trim(),
                    itemType: Number(item.itemType),
                    gender: Number(item.gender),
                    ...(item.id ? { avatarItemId: item.id } : {
                        accessType: 1,
                        tiles: 1,
                        photoUrls: [item.url],
                        published: true,
                        specialStoreOpeningId: openingId.current,
                    }),
                    xOffset: Number(item.x),
                    yPos: Number(item.y),
                    price: Number(item.price),
                },
            }).then(({ data }) => {
                const result = item.id ? data.editAvatarItem : data.createAvatarItem;
                if (!result || (result.errors && result.errors.length) || !result.avatarItem) {
                    throw new Error(result && result.errors && result.errors.length ? result.errors.join(', ') : 'Could not save item.');
                }
                savedItems.current[index] = result.avatarItem.id;
                saveItem(index + 1);
            }).catch(onError);
        };

        const deleteItem = (index) => {
            if (index === removedIds.length) {
                saveItem(0);
                return;
            }
            const id = removedIds[index];
            if (deletedIds.current.includes(id)) {
                deleteItem(index + 1);
                return;
            }
            client.mutate({ mutation: DELETE_AVATAR_ITEM, variables: { avatarItemId: id } })
                .then(({ data }) => {
                    const result = data.deleteAvatarItem;
                    if (!result || !result.deleted || (result.errors && result.errors.length)) {
                        throw new Error(result && result.errors && result.errors.length ? result.errors.join(', ') : 'Could not delete item.');
                    }
                    deletedIds.current.push(id);
                    deleteItem(index + 1);
                }).catch(onError);
        };

        if (opening) {
            client.mutate({
                mutation: EDIT_SPECIAL_STORE_OPENING,
                variables: { specialStoreOpeningId: openingId.current, startDate, endDate },
            }).then(({ data }) => {
                const result = data.editSpecialStoreOpening;
                if (!result || !result.specialStoreOpening || (result.errors && result.errors.length)) {
                    throw new Error(result && result.errors && result.errors.length ? result.errors.join(', ') : 'Could not update special store.');
                }
                setStarted(true);
                deleteItem(0);
            }).catch(onError);
            return;
        }
        if (openingId.current) {
            saveItem(0);
            return;
        }
        client.mutate({
            mutation: CREATE_SPECIAL_STORE_OPENING,
            variables: { startDate, endDate },
        }).then(({ data }) => {
            const result = data.createSpecialStoreOpening;
            if (!result || (result.errors && result.errors.length) || !result.specialStoreOpening) {
                throw new Error(result && result.errors && result.errors.length ? result.errors.join(', ') : 'Could not create special store.');
            }
            openingId.current = result.specialStoreOpening.id;
            setStarted(true);
            saveItem(0);
        }).catch(onError);
    };

    return (
        <>
            <BackLink to='/boomerItems/specialStore' text='Back to Drafts' />
            <div className={styles.sectionHeader}>
                <h1 className={styles.headerTitle}>Overview/Boomer Items/Special Store/{opening ? 'Edit' : 'New'}</h1>
                <button className={`${styles.actionButton} ${styles.publishButton}`} type='submit' form='special-store-form' disabled={uploading || saving || complete}>
                    {saving ? 'Saving...' : complete ? 'Created' : started ? 'Retry Save' : opening ? 'Save Changes' : 'Create Store'}
                </button>
            </div>
            {error && <p className={styles.fileError} role='alert'>{error}</p>}
            {started && !complete && !saving && <p>Keep this page open and retry to finish saving the remaining items.</p>}
            {complete && <p role='status'>Special store saved.</p>}
            {removedIds.length > 0 && <p>{removedIds.length} items will be deleted when you save changes.</p>}
            <form id='special-store-form' onSubmit={createStore}>
                <div className={styles.addItemLayout}>
                    <div className={styles.addItemForm}>
                        <h2>Sale Window</h2>
                        <div className={styles.specialStoreItem}>
                            <div className={`${styles.formGroup} ${styles.specialStoreItem}`}>
                                <label htmlFor='store-start'>Start Date</label>
                                <input id='store-start' className={`${styles.formInput} basic-form__text-box`} type='date' value={startDate} disabled={locked || started} onChange={event => setStartDate(event.target.value)} required />
                            </div>
                            <div className={`${styles.formGroup} ${styles.specialStoreItem}`}>
                                <label htmlFor='store-end'>End Date</label>
                                <input id='store-end' className={`${styles.formInput} basic-form__text-box`} type='date' min={startDate} value={endDate} disabled={locked || started} onChange={event => setEndDate(event.target.value)} required />
                            </div>
                        </div>
                    </div>
                    <div className={styles.boomerPreview}>
                        <BoomerItemPreview className={styles.previewAvatar} bodySrc={isSkin && selectedItem.preview ? selectedItem.preview : skin ? skin.preview : defaultBoomer} itemSrc={selectedItem && !isSkin ? selectedItem.preview : ''} coordinates={{ x: Number(selectedItem.x) || 0, y: Number(selectedItem.y) || 0 }} itemName={selectedItem.name || 'Item'} />
                        {selectedItem.preview && selectedItem.itemType && !isSkin && (
                            <div className={styles.positionControls}>
                                <div className={styles.positionArrows}>
                                    <button type='button' disabled={positionLocked} aria-label='Move item up' onClick={() => moveItem(0, -1)}>↑</button>
                                    <button type='button' disabled={positionLocked} aria-label='Move item left' onClick={() => moveItem(-1, 0)}>←</button>
                                    <button type='button' disabled={positionLocked} aria-label='Move item right' onClick={() => moveItem(1, 0)}>→</button>
                                    <button type='button' disabled={positionLocked} aria-label='Move item down' onClick={() => moveItem(0, 1)}>↓</button>
                                </div>
                                <div className={styles.positionValues}>
                                    {['x', 'y'].map(axis => <label key={axis}>{axis.toUpperCase()}:<input type='text' value={selectedItem[axis]} disabled={positionLocked} onChange={event => changePosition(axis, event.target.value)} /></label>)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <h2>Items</h2>
                <div className={styles.specialStoreItems}>
                    {items.map((item, index) => (
                        <fieldset key={index} className={styles.specialStoreItem} disabled={locked || Boolean(savedItems.current[index])}>
                            <legend>Item {index + 1}</legend>
                            <select id={`item-type-${index}`} aria-label={`Item ${index + 1} type`} className={`${styles.formInput} basic-form__text-select`} value={item.itemType} onChange={event => selectType(index, event.target.value)} required={hasItemDetails(item)}>
                                <option value=''>Item Type</option>
                                {Number(item.itemType) === 5 && <option value='5'>Accessory</option>}
                                {itemTypes.map(type => <option key={type.key} value={type.itemType}>{type.itemType === 2 ? 'Hair' : type.itemName}</option>)}
                            </select>
                            <label htmlFor={`item-name-${index}`}>Name</label>
                            <input id={`item-name-${index}`} className={`${styles.formInput} basic-form__text-box`} value={item.name} onChange={event => updateItem(index, { name: event.target.value })} required={hasItemDetails(item)} />
                            <select id={`item-gender-${index}`} aria-label={`Item ${index + 1} gender`} className={`${styles.formInput} basic-form__text-select`} value={item.gender} onChange={event => updateItem(index, { gender: event.target.value })} required={hasItemDetails(item)}>
                                <option value=''>Gender</option><option value='1'>Female</option><option value='2'>Male</option><option value='3'>Mixed</option>
                            </select>
                            {item.preview ? (
                                <button className={`${styles.itemUpload} ${styles.categoryCard}`} type='button' aria-label={`Preview and position item ${index + 1}`} aria-pressed={index === selectedIndex} onClick={() => setSelectedIndex(index)}>
                                    <img className={styles.uploadedItem} src={item.preview} alt={item.name || `Item ${index + 1}`} />
                                </button>
                            ) : (
                                <div className={styles.itemUpload}>
                                    <input className={styles.itemFileInput} type='file' disabled={Boolean(item.id)} accept='.jpg,.jpeg,.png,.svg' aria-label={`Upload image for item ${index + 1}`} onChange={event => uploadItem(event, index)} />
                                    <img className={styles.uploadIcon} src={plusCircled} alt='' />
                                </div>
                            )}
                            <label htmlFor={`item-price-${index}`}>Gem Cost</label>
                            <input id={`item-price-${index}`} className={`${styles.formInput} basic-form__text-box`} type='number' min='0' step='1' value={item.price} onChange={event => updateItem(index, { price: event.target.value })} required={hasItemDetails(item)} />
                            <button type='button' disabled={!hasItemDetails(item)} onClick={() => {
                                if (item.id) setRemovedIds(previous => [...previous, item.id]);
                                updateItem(index, newItem());
                            }}>{item.id ? 'Delete' : 'Clear'}</button>
                        </fieldset>
                    ))}
                </div>

                {uploading && <p className={styles.uploadStatus}>Uploading...</p>}
            </form>
        </>
    );
};

export default AddSpecialStore;
